import { Entry } from 'contentful';
import { NextPageWithLayout } from 'next';
import NextImage from 'next/image';

import { AppDownloadSection } from '../../components/AppDownloadSection/AppDownloadSection';
import { BlogPostSection } from '../../components/Blog/BlogPostSection/BlogPostSection';
import { Container } from '../../components/Container/Container';
import { FindUSFishingSpot } from '../../components/FindUSFishingSpot/FindUSFishingSpot';
import { IAllStateWaterData } from '../../components/FindUSFishingSpot/helpers/buildStateDataWaters';
import { FindUSState } from '../../components/FindUSState/FindUSState';
import { FishingWaterCarousel } from '../../components/FishingWaterCarousel/FishingWaterCarousel';
import { Heading } from '../../components/Heading/Heading';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { PlaceCarousel } from '../../components/PlaceCarousel/PlaceCarousel';
import { SEOLinksFooter } from '../../components/SEOFooter/SEOLinksFooter';
import { USsilhouette } from '../../components/Silhouettes/US';
import { USStateObject } from '../../constants/USstates/constants';
import { addDefiniteArticleToCountryName } from '../../helpers/country/addDefiniteArticleToCountryName';
import { getEdgesFromConnection, getNodesFromConnection } from '../../helpers/graphql';
import { getCountryUrl } from '../../helpers/urls';
import { TypeBlogPostSkeleton } from '../../interfaces/contentful';
import {
  GetCountry_entry,
  GetCountry_entry_places_edges,
  PlaceCarouselItem,
} from '../../interfaces/graphql';

import headerBackground from './assets/headerBackground.png';

export interface ICountryProps {
  country: GetCountry_entry;
  usStates: USStateObject[];
  stateWaterData: IAllStateWaterData;
  defaultUSState: USStateObject;
  blogPostItems: Entry<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>[] | undefined;
}
export const Country: NextPageWithLayout<ICountryProps> = ({
  country,
  usStates,
  stateWaterData,
  defaultUSState,
  blogPostItems,
}) => {
  const { countryName, topFishingWaters, places, id } = country;
  const waters = getNodesFromConnection(topFishingWaters);
  const placeEdges = getEdgesFromConnection<PlaceCarouselItem, GetCountry_entry_places_edges>(
    places,
  );
  const metaTemplate = {
    title: `Fishing in ${addDefiniteArticleToCountryName(countryName, id)} ➡️️️️ Explore the best fishing spots and areas`,
    description: `Fishbrain ➡ The best fishing app and community in ${addDefiniteArticleToCountryName(countryName, id)} ✅ Discover the best fishing spots, top species caught, user tips, and more • Register for Free!`,
  };
  const headingText = `Fishing in ${addDefiniteArticleToCountryName(countryName, id)}`;

  return (
    <>
      <MetaTags
        title={metaTemplate.title}
        description={metaTemplate.description}
        link={getCountryUrl(id, countryName)}
      />
      <Container className="-px-6 sm:-px-11 md:-px-14">
        <div className="relative w-full bg-cover p-4 text-center sm:px-11 sm:py-6 md:px-14 md:py-8">
          <NextImage
            alt="most liked catch image"
            className="size-full object-cover"
            src={headerBackground}
            fill
            priority
            placeholder="blur"
            sizes="100vw"
          />
          <div className="relative flex justify-between">
            <Heading as="h1" className="m-0 w-11/12 text-left text-white sm:w-1/2">
              {headingText}
            </Heading>
            {id === 'US' && <USsilhouette fillColour="#FFF" />}
          </div>
        </div>
      </Container>

      {id === 'US' && (
        <>
          <FindUSState usStates={usStates} />
          <FindUSFishingSpot
            stateWaterData={stateWaterData}
            usStates={usStates}
            defaultUSState={defaultUSState}
          />
        </>
      )}

      <FishingWaterCarousel
        dataTestId="country-top-waters"
        title={`Top Fishing spots in ${addDefiniteArticleToCountryName(countryName, id)}`}
        clickEventSource="country-top-waters"
        clickEventSourceId={id}
        waters={waters}
      />

      {placeEdges.length > 0 && (
        <PlaceCarousel
          dataTestId="country-cities"
          title={`Cities in ${addDefiniteArticleToCountryName(countryName, id)}`}
          clickEventSource="country-cities"
          clickEventSourceId={id}
          placeEdges={placeEdges}
        />
      )}

      {blogPostItems && blogPostItems.length > 0 && (
        <BlogPostSection blogPostItems={blogPostItems} linkTo="/blog" showCountry />
      )}

      <AppDownloadSection hasAppRating />
      <SEOLinksFooter />
    </>
  );
};
